const hexToRgb = (hex: string) => {
  const cleanHex = hex.replace('#', '')
  return {
    r: parseInt(cleanHex.substring(0, 2), 16),
    g: parseInt(cleanHex.substring(2, 4), 16),
    b: parseInt(cleanHex.substring(4, 6), 16)
  }
}

const rgbToHex = (r: number, g: number, b: number) => {
  const toHex = (value: number) => {
    const hex = Math.round(value).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

// https://en.wikipedia.org/wiki/Relative_luminance
// Luminance calculation constants based on sRGB color space and human vision perception
const SRGB_GAMMA_THRESHOLD = 0.03928 // Threshold for linear vs gamma correction
const SRGB_GAMMA_LINEAR_COEFFICIENT = 12.92 // Linear coefficient for small values
const SRGB_GAMMA_OFFSET = 0.055 // Gamma correction offset
const SRGB_GAMMA_DIVISOR = 1.055 // Gamma correction divisor
const SRGB_GAMMA_EXPONENT = 2.4 // Gamma correction exponent

// CIE luminance weights for RGB channels (based on human eye sensitivity)
const LUMINANCE_WEIGHT_RED = 0.2126 // Red channel weight
const LUMINANCE_WEIGHT_GREEN = 0.7152 // Green channel weight (highest due to eye sensitivity)
const LUMINANCE_WEIGHT_BLUE = 0.0722 // Blue channel weight

const calculateLuminance = (r: number, g: number, b: number): number => {
  const normalize = (value: number): number => {
    const normalized = value / 255
    return normalized <= SRGB_GAMMA_THRESHOLD
      ? normalized / SRGB_GAMMA_LINEAR_COEFFICIENT
      : Math.pow((normalized + SRGB_GAMMA_OFFSET) / SRGB_GAMMA_DIVISOR, SRGB_GAMMA_EXPONENT)
  }

  const rNorm = normalize(r)
  const gNorm = normalize(g)
  const bNorm = normalize(b)

  return (
    LUMINANCE_WEIGHT_RED * rNorm + LUMINANCE_WEIGHT_GREEN * gNorm + LUMINANCE_WEIGHT_BLUE * bNorm
  )
}

const isLightColor = (hex: string): boolean => {
  const rgb = hexToRgb(hex)
  if (!rgb) return false

  const luminance = calculateLuminance(rgb.r, rgb.g, rgb.b)
  return luminance > 0.5
}

export const getContrastingColor = (backgroundColor: string) => {
  const isLight = isLightColor(backgroundColor)

  return isLight ? '#1a1a1a' : '#ffffff'
}

const MAX_HEX_COLOR_VALUE = 16777215
const HEX_COLOR_LENGTH = 6
const HEX_BASE = 16

const generateRandomColor = () => {
  return (
    '#' +
    Math.floor(Math.random() * MAX_HEX_COLOR_VALUE)
      .toString(HEX_BASE)
      .padStart(HEX_COLOR_LENGTH, '0')
  )
}

export const getRandomColor = () => {
  const color = generateRandomColor()

  return {
    color: getContrastingColor(color),
    background: color
  }
}

const HUE_MAX = 360
const SATURATION_PERCENTAGE = 100
const LIGHTNESS_PERCENTAGE = 100

/**
 * Converts HSL color values to RGB
 * @param h - Hue (0-360)
 * @param s - Saturation (0-100)
 * @param l - Lightness (0-100)
 * @returns RGB object with r, g, b values (0-255)
 */
const hslToRgb = (h: number, s: number, l: number) => {
  const hNorm = h / HUE_MAX
  const sNorm = s / SATURATION_PERCENTAGE
  const lNorm = l / LIGHTNESS_PERCENTAGE

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm
  const x = c * (1 - Math.abs(((hNorm * 6) % 2) - 1))
  const m = lNorm - c / 2

  let r = 0,
    g = 0,
    b = 0

  if (hNorm >= 0 && hNorm < 1 / 6) {
    r = c
    g = x
    b = 0
  } else if (hNorm >= 1 / 6 && hNorm < 2 / 6) {
    r = x
    g = c
    b = 0
  } else if (hNorm >= 2 / 6 && hNorm < 3 / 6) {
    r = 0
    g = c
    b = x
  } else if (hNorm >= 3 / 6 && hNorm < 4 / 6) {
    r = 0
    g = x
    b = c
  } else if (hNorm >= 4 / 6 && hNorm < 5 / 6) {
    r = x
    g = 0
    b = c
  } else {
    r = c
    g = 0
    b = x
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  }
}

const DEFAULT_SATURATION = 70
const DEFAULT_LIGHTNESS = 50

/**
 * Generates a rainbow palette with evenly distributed, contrasting colors
 * @param length - Number of colors to generate
 * @param saturation - Color saturation (0-100, default: 70)
 * @param lightness - Color lightness (0-100, default: 50)
 * @returns Array of hex color strings
 */

export const generateRainbowPalette = (
  length: number,
  options?: {
    saturation?: number
    lightness?: number
  }
) => {
  const saturation = options?.saturation || DEFAULT_SATURATION
  const lightness = options?.lightness || DEFAULT_LIGHTNESS

  if (length <= 0) return []
  if (length === 1) return ['#ff0000']

  const colors: string[] = []
  const hueStep = HUE_MAX / length

  for (let i = 0; i < length; i++) {
    const hue = (i * hueStep) % HUE_MAX
    const rgb = hslToRgb(hue, saturation, lightness)
    colors.push(rgbToHex(rgb.r, rgb.g, rgb.b))
  }

  return colors
}
